import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'

export const Contrast = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        select('[data-contrast-block]').forEach((block) => {
            const trigger = { trigger: block, start: 'top 78%' }

            gsap.from(block.querySelectorAll('[data-contrast-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1,
                stagger: 0.045,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(block.querySelector('[data-contrast-body]'), {
                y: 24,
                opacity: 0,
                duration: 0.9,
                delay: 0.18,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            const media = block.querySelector('[data-contrast-media]')

            if (!media) return

            gsap.fromTo(
                media,
                { clipPath: 'inset(0% 0% 100% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.4,
                    ease: 'nuvea',
                    scrollTrigger: { trigger: media, start: 'top 86%' }
                }
            )

            // A slow counter-drift inside the frame reads as depth rather than motion.
            gsap.fromTo(
                media.querySelector('img'),
                { yPercent: -8, scale: 1.16 },
                {
                    yPercent: 8,
                    scale: 1.16,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: media,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.8
                    }
                }
            )
        })
    })

    return (
        <section
            ref={scope}
            id='less-is-more'
            className='bg-shell py-24 md:py-32'
        >
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <div
                    data-contrast-block=''
                    className='grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16'
                >
                    <div className='order-2 lg:order-1'>
                        <SplitWords
                            data-contrast-title=''
                            as='h2'
                            lines={['Most routines are', 'overloaded.']}
                            className='type-h1 max-w-[14ch] text-ink'
                        />

                        <p
                            data-contrast-body=''
                            className='type-body mt-6 max-w-[50ch] text-slate-soft'
                        >
                            There is a reason your skin got worse the more you bought. Most brands add ingredients
                            hoping a few will land, and the barrier spends its energy coping
                            with the traffic instead of repairing itself.
                        </p>
                    </div>

                    <div
                        data-contrast-media=''
                        className='relative order-1 aspect-[3/2] w-full overflow-hidden rounded-2xl bg-shell lg:order-2'
                    >
                        <Image
                            src='/images/skincare-overload.png'
                            alt='A woman holding a jar of Nuvéa, looking away in soft daylight'
                            fill
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            className='object-cover will-change-transform'
                        />
                    </div>
                </div>

                <div
                    data-contrast-block=''
                    className='mt-16 grid grid-cols-1 items-center gap-10 md:mt-24 lg:grid-cols-2 lg:gap-16'
                >
                    <div
                        data-contrast-media=''
                        className='relative aspect-[3/2] w-full overflow-hidden rounded-2xl bg-shell'
                    >
                        <Image
                            src='/images/skincare-less.png'
                            alt='A woman resting a jar of Nuvéa against her collarbone, calm and unhurried'
                            fill
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            className='object-cover will-change-transform'
                        />
                    </div>

                    <div>
                        <SplitWords
                            data-contrast-title=''
                            as='h2'
                            lines={['What if restraint', 'worked harder?']}
                            className='type-h1 max-w-[14ch] text-ink'
                        />

                        <p
                            data-contrast-body=''
                            className='type-body mt-6 max-w-[50ch] text-slate-soft'
                        >
                            We cut until five were left, each chosen for measurable effect on the barrier rather than
                            for how it reads. The result is skin in genuinely better
                            condition, not skin that is being temporarily covered.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
